import { useState, useEffect } from "react";
import {
  useSettings,
  CheckoutOptions,
  DeliveryLocation,
  BankAccount,
} from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Trash, Plus, Loader } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

export function CheckoutSettings() {
  const { checkoutOptions, isUpdating, updateCheckoutOptions } = useSettings();

  const [formData, setFormData] = useState<CheckoutOptions>({
    productDelivery: {
      enableDelivery: checkoutOptions.productDelivery?.enableDelivery ?? true,
      enablePickup: checkoutOptions.productDelivery?.enablePickup ?? true,
      deliveryLocations:
        checkoutOptions.productDelivery?.deliveryLocations ?? [],
    },
    paymentOptions: {
      bankTransfer: {
        enabled: checkoutOptions.paymentOptions?.bankTransfer?.enabled ?? false,
        accounts: checkoutOptions.paymentOptions?.bankTransfer?.accounts ?? [],
        accountInfo:
          checkoutOptions.paymentOptions?.bankTransfer?.accountInfo ?? "",
      },
      cashOnDelivery: {
        enabled:
          checkoutOptions.paymentOptions?.cashOnDelivery?.enabled ?? false,
      },
      manualPayment: {
        enabled:
          checkoutOptions.paymentOptions?.manualPayment?.enabled ?? false,
        instructions:
          checkoutOptions.paymentOptions?.manualPayment?.instructions ?? "",
      },
    },
    whatsapp: {
      enabled: checkoutOptions.whatsapp?.enabled ?? true,
    },
  });

  const [newLocation, setNewLocation] = useState<DeliveryLocation>({
    name: "",
    price: 0,
  });

  const [newBankAccount, setNewBankAccount] = useState<BankAccount>({
    accountName: "",
    accountNumber: "",
    bankName: "",
  });

  // Reset form data when checkout options change
  useEffect(() => {
    setFormData({
      productDelivery: {
        enableDelivery: checkoutOptions.productDelivery?.enableDelivery ?? true,
        enablePickup: checkoutOptions.productDelivery?.enablePickup ?? true,
        deliveryLocations:
          checkoutOptions.productDelivery?.deliveryLocations ?? [],
      },
      paymentOptions: {
        bankTransfer: {
          enabled:
            checkoutOptions.paymentOptions?.bankTransfer?.enabled ?? false,
          accounts:
            checkoutOptions.paymentOptions?.bankTransfer?.accounts ?? [],
          accountInfo:
            checkoutOptions.paymentOptions?.bankTransfer?.accountInfo ?? "",
        },
        cashOnDelivery: {
          enabled:
            checkoutOptions.paymentOptions?.cashOnDelivery?.enabled ?? false,
        },
        manualPayment: {
          enabled:
            checkoutOptions.paymentOptions?.manualPayment?.enabled ?? false,
          instructions:
            checkoutOptions.paymentOptions?.manualPayment?.instructions ?? "",
        },
      },
      whatsapp: {
        enabled: checkoutOptions.whatsapp?.enabled ?? true,
      },
    });
  }, [checkoutOptions]);

  const handleToggleChange = (
    section: "productDelivery" | "paymentOptions",
    field: string,
    value: boolean
  ) => {
    setFormData((prev) => {
      // Handle nested payment options
      if (section === "paymentOptions") {
        return {
          ...prev,
          paymentOptions: {
            ...prev.paymentOptions,
            [field]: {
              ...prev.paymentOptions?.[
                field as keyof typeof prev.paymentOptions
              ],
              enabled: value,
            },
          },
        };
      }

      // Handle productDelivery
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      };
    });
  };

  const handlePaymentInstructionsChange = (
    paymentType: string,
    instructions: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      paymentOptions: {
        ...prev.paymentOptions,
        [paymentType]: {
          ...prev.paymentOptions?.[
            paymentType as keyof typeof prev.paymentOptions
          ],
          instructions,
        },
      },
    }));
  };

  const handleBankAccountInfoChange = (accountInfo: string) => {
    setFormData((prev) => ({
      ...prev,
      paymentOptions: {
        ...prev.paymentOptions,
        bankTransfer: {
          ...prev.paymentOptions?.bankTransfer,
          accountInfo,
        },
      },
    }));
  };

  const addBankAccount = () => {
    if (
      !newBankAccount.accountName.trim() ||
      !newBankAccount.accountNumber.trim() ||
      !newBankAccount.bankName.trim()
    ) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      paymentOptions: {
        ...prev.paymentOptions,
        bankTransfer: {
          ...prev.paymentOptions?.bankTransfer,
          accounts: [
            ...(prev.paymentOptions?.bankTransfer?.accounts || []),
            {
              accountName: newBankAccount.accountName.trim(),
              accountNumber: newBankAccount.accountNumber.trim(),
              bankName: newBankAccount.bankName.trim(),
            },
          ],
        },
      },
    }));

    setNewBankAccount({
      accountName: "",
      accountNumber: "",
      bankName: "",
    });
  };

  const removeBankAccount = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      paymentOptions: {
        ...prev.paymentOptions,
        bankTransfer: {
          ...prev.paymentOptions?.bankTransfer,
          accounts:
            prev.paymentOptions?.bankTransfer?.accounts?.filter(
              (_, i) => i !== index
            ) || [],
        },
      },
    }));
  };

  const addLocation = () => {
    if (newLocation.name.trim() && newLocation.price >= 0) {
      setFormData((prev) => ({
        ...prev,
        productDelivery: {
          ...prev.productDelivery,
          deliveryLocations: [
            ...(prev.productDelivery?.deliveryLocations || []),
            { ...newLocation, price: Number(newLocation.price) },
          ],
        },
      }));
      setNewLocation({ name: "", price: 0 });
    }
  };

  const removeLocation = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      productDelivery: {
        ...prev.productDelivery,
        deliveryLocations:
          prev.productDelivery?.deliveryLocations?.filter(
            (_, i) => i !== index
          ) || [],
      },
    }));
  };

  const handleSubmit = () => {
    // Transform form data for API
    const updateData = {
      productDeliveryOptions: {
        enableDelivery: Boolean(formData.productDelivery?.enableDelivery),
        enablePickup: Boolean(formData.productDelivery?.enablePickup),
        deliveryLocations: (
          formData.productDelivery?.deliveryLocations || []
        ).map((loc) => ({
          name: loc.name,
          price: Number(loc.price),
        })),
      },
      paymentOptions: {
        bankTransfer: {
          enabled: Boolean(formData.paymentOptions?.bankTransfer?.enabled),
          accounts:
            formData.paymentOptions?.bankTransfer?.accounts?.map((account) => ({
              accountName: account.accountName,
              accountNumber: account.accountNumber,
              bankName: account.bankName,
            })) || [],
          accountInfo: formData.paymentOptions?.bankTransfer?.accountInfo || "",
        },
        cashOnDelivery: {
          enabled: Boolean(formData.paymentOptions?.cashOnDelivery?.enabled),
        },
        manualPayment: {
          enabled: Boolean(formData.paymentOptions?.manualPayment?.enabled),
          instructions:
            formData.paymentOptions?.manualPayment?.instructions || "",
        },
      },
      whatsapp: {
        enabled: Boolean(formData.whatsapp?.enabled),
      },
    };

    updateCheckoutOptions(updateData);
  };

  return (
    <div className="space-y-8">
      <section>
        <p className="text-base font-medium mb-3">Product Orders</p>
        <div className="space-y-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="enable-whatsapp">Enable WhatsApp Checkout</Label>
              <p className="text-sm text-muted-foreground">
                Allow customers to place orders via WhatsApp
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="enable-whatsapp"
                checked={formData.whatsapp?.enabled}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({
                    ...prev,
                    whatsapp: { enabled: checked },
                  }))
                }
              />
            </div>
          </div>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="enable-delivery">Enable Delivery</Label>
              <p className="text-sm text-muted-foreground">
                Allow customers to have products delivered to their location
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="enable-delivery"
                checked={formData.productDelivery?.enableDelivery}
                onCheckedChange={(checked) =>
                  handleToggleChange(
                    "productDelivery",
                    "enableDelivery",
                    checked
                  )
                }
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="enable-pickup">Enable Pickup</Label>
              <p className="text-sm text-muted-foreground">
                Allow customers to pick up products from your location
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="enable-pickup"
                checked={formData.productDelivery?.enablePickup}
                onCheckedChange={(checked) =>
                  handleToggleChange("productDelivery", "enablePickup", checked)
                }
              />
            </div>
          </div>

          {formData.productDelivery?.enableDelivery && (
            <>
              {" "}
              <p className="text-base font-medium">Delivery Locations</p>
              <div className="space-y-2">
                {formData.productDelivery?.deliveryLocations?.map(
                  (location, index) => (
                    <div
                      key={`${location.name}-${index}`}
                      className="flex bg-primary/10 hover:bg-primary/20 transition-all duration-200 ease-in-out flex-col gap-3 rounded-lg border border-dashed border-border/60 px-2 sm:flex-row sm:items-center"
                    >
                      <div className="flex-1">
                        <span className="text-sm font-medium">
                          {location.name} :
                        </span>
                        <span className="text-sm text-muted-foreground ml-2">
                          ₦{location.price}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLocation(index)}
                        className="text-red-600 hover:text-red-700 "
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  )
                )}

                <div className="flex flex-col gap-4 mt-4 sm:flex-row sm:items-end">
                  <div className="flex-1">
                    <Label htmlFor="location-name">Location Name</Label>
                    <Input
                      id="location-name"
                      placeholder="e.g., Lagos Island"
                      value={newLocation.name}
                      className="!h-10 mt-2 border-gray-300 text-sm"
                      onChange={(e) =>
                        setNewLocation((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="sm:w-32">
                    <Label htmlFor="location-price">Price (₦)</Label>
                    <Input
                      id="location-price"
                      type="number"
                      placeholder="0"
                      value={newLocation.price}
                      className="!h-10 mt-2 border-gray-300 text-sm"
                      onChange={(e) =>
                        setNewLocation((prev) => ({
                          ...prev,
                          price: Number(e.target.value),
                        }))
                      }
                    />
                  </div>
                  <Button onClick={addLocation} size="sm" className="!h-10">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      <section>
        <p className="text-base font-medium mb-3">Payment Options</p>
        <div className="space-y-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="cash-on-delivery">Cash on Delivery</Label>
              <p className="text-sm text-muted-foreground">
                Accept cash payments upon delivery
              </p>
            </div>
            <Switch
              id="cash-on-delivery"
              checked={formData.paymentOptions?.cashOnDelivery?.enabled}
              onCheckedChange={(checked) =>
                handleToggleChange("paymentOptions", "cashOnDelivery", checked)
              }
            />
          </div>

          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="bank-transfer">Bank Transfer</Label>
              <p className="text-sm text-muted-foreground">
                Accept payments via bank transfer
              </p>
            </div>
            <Switch
              id="bank-transfer"
              checked={formData.paymentOptions?.bankTransfer?.enabled}
              onCheckedChange={(checked) =>
                handleToggleChange("paymentOptions", "bankTransfer", checked)
              }
            />
          </div>

          {formData.paymentOptions?.bankTransfer?.enabled && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Add one or more bank accounts customers can transfer payments
                to.
              </p>

              <div className="space-y-2">
                {formData.paymentOptions?.bankTransfer?.accounts?.map(
                  (account, index) => (
                    <div
                      key={`${account.accountNumber}-${index}`}
                      className="flex flex-col gap-2 rounded-lg border border-dashed border-border/60 bg-primary/10 p-3 sm:flex-row sm:items-center"
                    >
                      <div className="flex-1 text-sm">
                        <p className="font-semibold">{account.accountName}</p>
                        <p className="text-muted-foreground">
                          {account.accountNumber}
                        </p>
                        <p className="text-muted-foreground">
                          {account.bankName}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeBankAccount(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  )
                )}
              </div>
              {formData.paymentOptions?.bankTransfer?.accounts?.length < 3 ? (
                <div className="flex flex-col gap-4 mt-4 sm:flex-row sm:items-end">
                  <div className="space-y-2">
                    <Label htmlFor="bank-account-name">Account Name</Label>
                    <Input
                      id="bank-account-name"
                      placeholder="e.g., Adeola Bakare"
                      value={newBankAccount.accountName}
                      className="!h-10 mt-2 border-gray-300 text-sm"
                      onChange={(e) =>
                        setNewBankAccount((prev) => ({
                          ...prev,
                          accountName: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank-account-number">Account Number</Label>
                    <Input
                      id="bank-account-number"
                      placeholder="e.g., 0123456789"
                      value={newBankAccount.accountNumber}
                      className="!h-10 mt-2 border-gray-300 text-sm"
                      onChange={(e) =>
                        setNewBankAccount((prev) => ({
                          ...prev,
                          accountNumber: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank-name">Bank Name</Label>
                    <Input
                      id="bank-name"
                      placeholder="e.g., Access Bank"
                      value={newBankAccount.bankName}
                      className="!h-10 mt-2 border-gray-300 text-sm"
                      onChange={(e) =>
                        setNewBankAccount((prev) => ({
                          ...prev,
                          bankName: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="">
                    <Button
                      onClick={addBankAccount}
                      size="sm"
                      className="!h-10"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  You can only add up to 3 bank accounts
                </p>
              )}

              <div className="space-y-2">
                <Label htmlFor="bank-account-info">
                  Additional Instructions (optional)
                </Label>
                <Textarea
                  id="bank-account-info"
                  placeholder="Share any transfer instructions customers should follow"
                  value={
                    formData.paymentOptions?.bankTransfer?.accountInfo || ""
                  }
                  onChange={(e) => handleBankAccountInfoChange(e.target.value)}
                  className="min-h-[80px] border-gray-300 text-sm"
                />
              </div>
            </div>
          )}
        </div>
      </section>

      <div className="flex justify-end pt-4">
        <Button onClick={handleSubmit} disabled={isUpdating} className="!h-10">
          {isUpdating ? (
            <>
              <Loader className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </div>
    </div>
  );
}
